import type { CarePlan, Extension } from '@medplum/fhirtypes';

import { CARE_PLAN_MATCH_EXTENSION_URL } from './constants';

// The AI matcher's verdict, carried as a complex extension on the draft CarePlan
// (ENG-4699). Medplum-only state: no Postgres table holds the score/rationale —
// the recommendation card and callout read it straight off the CarePlan. The
// backend matcher writes it; the frontend recommendation card decodes it.

export type CarePlanMatchRationale = {
  // Short bold lead-in, e.g. "Chief Complaint".
  label: string;
  // The grounded justification, e.g. "Reports 3 weeks of right hip pain…".
  text: string;
};

// One entry in the matcher's ranked pool (ENG-4764): a candidate template the
// matcher considered, with its score and a one-line reason. The winner is also
// carried here so switching is uniform — the active plan is identified by the
// CarePlan's `instantiatesCanonical`, and every OTHER candidate is offered as a
// switchable alternative in the Care Plan tab.
export type CarePlanMatchCandidate = {
  // Pinned canonical (`url|version`) of the candidate PlanDefinition.
  canonical: string;
  // Human title, joined in by the generation service (the matcher echoes only
  // canonicals) so the alternatives list renders without fetching every PD.
  title: string;
  // Matcher confidence in [0, 1].
  confidence: number;
  // One short sentence: why this template ranked where it did for the visit.
  rationale: string;
};

export type CarePlanMatch = {
  // Matcher confidence in [0, 1]; the callout renders it as a percentage.
  confidence: number;
  rationale: CarePlanMatchRationale[];
  // The full ranked pool (winner included), best-first. Empty for drafts
  // generated before ENG-4764 — the UI then shows just the single match.
  candidates: CarePlanMatchCandidate[];
};

export function encodeCarePlanMatchExtension(match: CarePlanMatch): Extension {
  return {
    url: CARE_PLAN_MATCH_EXTENSION_URL,
    extension: [
      { url: 'confidence', valueDecimal: match.confidence },
      ...match.rationale.map(
        (r): Extension => ({
          url: 'rationale',
          extension: [
            { url: 'label', valueString: r.label },
            { url: 'text', valueString: r.text },
          ],
        }),
      ),
      // The ranked pool (ENG-4764), one repeated `candidate` sub-extension each.
      // Additive to the same extension URL: older `decodeCarePlanMatch` builds
      // ignore unknown sub-extensions, so old web bundles keep decoding cleanly.
      ...match.candidates.map(
        (c): Extension => ({
          url: 'candidate',
          extension: [
            { url: 'canonical', valueString: c.canonical },
            { url: 'title', valueString: c.title },
            { url: 'confidence', valueDecimal: c.confidence },
            { url: 'rationale', valueString: c.rationale },
          ],
        }),
      ),
    ],
  };
}

export function decodeCarePlanMatch(carePlan: CarePlan): CarePlanMatch | null {
  const ext = carePlan.extension?.find(
    (e) => e.url === CARE_PLAN_MATCH_EXTENSION_URL,
  );
  if (!ext) {
    return null;
  }
  const confidence = ext.extension?.find(
    (e) => e.url === 'confidence',
  )?.valueDecimal;
  if (confidence === undefined) {
    return null;
  }
  const rationale = (ext.extension ?? [])
    .filter((e) => e.url === 'rationale')
    .map((r) => ({
      label: r.extension?.find((s) => s.url === 'label')?.valueString ?? '',
      text: r.extension?.find((s) => s.url === 'text')?.valueString ?? '',
    }))
    .filter((r) => r.label || r.text);
  // The ranked pool (ENG-4764). Absent on pre-ENG-4764 drafts → `[]`, which the
  // UI reads as "no alternatives" and falls back to the single match.
  const candidates = (ext.extension ?? [])
    .filter((e) => e.url === 'candidate')
    .map((c) => ({
      canonical:
        c.extension?.find((s) => s.url === 'canonical')?.valueString ?? '',
      title: c.extension?.find((s) => s.url === 'title')?.valueString ?? '',
      confidence:
        c.extension?.find((s) => s.url === 'confidence')?.valueDecimal ?? 0,
      rationale:
        c.extension?.find((s) => s.url === 'rationale')?.valueString ?? '',
    }))
    .filter((c) => c.canonical);
  return { confidence, rationale, candidates };
}
